import { ApiPromise } from '@polkadot/api';
import { EventEmitter } from 'events';
import {
  OptimisticVerifyResult,
  ProofProcessor,
  TransactionValidityError,
} from '../../types.js';
import {
  Groth16Config,
  Plonky2Config,
  ProofConfig,
  proofConfigurations,
  ProofOptions,
  ProofType,
  Risc0Config,
  TeeConfig,
  UltraplonkConfig,
  UltrahonkConfig,
} from '../../config/index.js';
import { decodeDispatchError } from '../transactions/errors/index.js';
import { DispatchError, Extrinsic } from '@polkadot/types/interfaces';
import {
  AccountConnection,
  EstablishedConnection,
  WalletConnection,
} from '../../api/connection/types.js';

export * from './runtimeVersion/index.js';
import { KeyringPair } from '@polkadot/keyring/types';
import { SubmittableExtrinsic } from '@polkadot/api/types';

export interface WaitForNodeToSyncOptions {
  /**
   * Maximum total wait time in ms before throwing. Default 300_000 (5 min).
   */
  timeoutMs?: number;
  /**
   * Delay between health polls in ms. Default 1000.
   */
  pollIntervalMs?: number;
  /**
   * Optional AbortSignal to cancel the wait early. When aborted the function
   * rejects with an `AbortError`.
   */
  signal?: AbortSignal;
}

/**
 * Waits for the zkVerify node to finish syncing. Polls `system.health` at a
 * fixed interval up to a bounded deadline; throws on timeout or abort.
 *
 * @param api - The ApiPromise instance.
 * @param options - Optional timeout, poll interval, and abort signal.
 * @returns A promise that resolves when the node is synced.
 * @throws {Error} If the deadline is reached before the node finishes syncing.
 * @throws {DOMException} `AbortError` if the supplied signal is aborted.
 */
export async function waitForNodeToSync(
  api: ApiPromise,
  options: WaitForNodeToSyncOptions = {},
): Promise<void> {
  const { timeoutMs = 300_000, pollIntervalMs = 1000, signal } = options;
  const deadline = Date.now() + timeoutMs;

  while (true) {
    if (signal?.aborted) {
      throw new DOMException('waitForNodeToSync aborted', 'AbortError');
    }
    if (Date.now() > deadline) {
      throw new Error(
        `waitForNodeToSync timed out after ${timeoutMs}ms; node still reports isSyncing.`,
      );
    }

    const health = await api.rpc.system.health();
    if (!health.isSyncing.isTrue) return;

    await sleep(pollIntervalMs, signal);
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('sleep aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('sleep aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export function getProofProcessor(proofType: ProofType): ProofProcessor {
  const config = proofConfigurations[proofType];
  if (!config) {
    throw new Error(`No config found for Proof Processor: ${proofType}`);
  }
  return config.processor;
}

export function getProofPallet(proofType: ProofType): string {
  const config = proofConfigurations[proofType as ProofType];
  if (!config) {
    throw new Error(`No config found for Proof Pallet: ${proofType}`);
  }
  return config.pallet;
}

export function checkReadOnly(
  connection: AccountConnection | WalletConnection | EstablishedConnection,
): void {
  if (
    (!('accounts' in connection) ||
      ('accounts' in connection && connection.accounts.size === 0)) &&
    !('injector' in connection)
  ) {
    throw new Error(
      'This action requires an active account. The session is currently in read-only mode because no account is associated with it. Please provide an account at session start, or add one to the current session using `addAccount`.',
    );
  }
}

let cachedVerifierPallets: Set<string> | null = null;

function getVerifierPallets(): Set<string> {
  if (cachedVerifierPallets) return cachedVerifierPallets;
  const entries = Object.values(proofConfigurations ?? {}) as ProofConfig[];
  cachedVerifierPallets = new Set(
    entries.map((cfg) => cfg.pallet).filter(Boolean),
  );
  return cachedVerifierPallets;
}

/**
 * Interprets a dry run response and returns whether it was successful and any error message.
 * @param api - The Polkadot.js API instance.
 * @param resultHex - The hex-encoded response from a dry run.
 * @param proofType
 * @returns An object containing `success` (boolean) and `message` (string).
 */
export async function interpretDryRunResponse(
  api: ApiPromise,
  resultHex: string,
  proofType?: ProofType,
): Promise<OptimisticVerifyResult> {
  const responseBytes = Uint8Array.from(
    Buffer.from(resultHex.replace(/^0x/, ''), 'hex'),
  );

  if (responseBytes.length === 0) {
    return {
      success: false,
      type: 'unknown_error',
      message: 'Empty dryRun response',
      verificationError: false,
    };
  }

  if (responseBytes[0] === 0x01) {
    let validityError: TransactionValidityError | null;

    try {
      validityError = api.registry.createType(
        'TransactionValidityError',
        responseBytes.slice(1),
      ) as unknown as TransactionValidityError;
    } catch {
      try {
        validityError = api.registry.createType(
          'SpRuntimeTransactionValidityError',
          responseBytes.slice(1),
        ) as unknown as TransactionValidityError;
      } catch {
        validityError = null;
      }
    }

    if (validityError?.isInvalid) {
      const code = `InvalidTransaction.${validityError?.asInvalid.type}`;
      return {
        success: false,
        type: 'validity_error',
        code,
        message: code,
        verificationError: false,
      };
    }
    if (validityError?.isUnknown) {
      const code = `UnknownTransaction.${validityError?.asUnknown.type}`;
      return {
        success: false,
        type: 'validity_error',
        code,
        message: code,
        verificationError: false,
      };
    }
    return {
      success: false,
      type: 'validity_error',
      message: validityError?.toString() ?? 'TransactionValidityError',
      verificationError: false,
    };
  }

  if (responseBytes[0] === 0x00 && responseBytes.length >= 2) {
    if (responseBytes[1] === 0x00) {
      return {
        success: true,
        type: 'ok',
        message: 'Optimistic Verification Successful!',
      };
    }

    if (responseBytes[1] === 0x01) {
      try {
        const dispatchErrorCodec = api.registry.createType(
          'DispatchError',
          responseBytes.slice(2),
        ) as DispatchError;

        const { code, message, section } = decodeDispatchError(
          api,
          dispatchErrorCodec,
        );

        const expectedPallet = proofType
          ? proofConfigurations?.[proofType]?.pallet
          : undefined;

        const isVerifier =
          !!section &&
          (section === expectedPallet || getVerifierPallets().has(section));

        return {
          success: false,
          type: 'dispatch_error',
          code,
          message,
          verificationError: isVerifier,
        };
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);

        return {
          success: false,
          type: 'exception',
          message,
          verificationError: false,
        };
      }
    }
  }

  return {
    success: false,
    type: 'unknown_error',
    message: `Unexpected response format: ${resultHex}`,
    verificationError: false,
  };
}

/**
 * Retrieves the selected account from the connection based on the provided account address.
 * If no account address is provided, it defaults to the first available account.
 *
 * @param {AccountConnection} connection - The connection containing account information.
 * @param {string | undefined} accountAddress - The optional account address to retrieve.
 * @returns {KeyringPair} - The selected account.
 * @throws {Error} If the account is not found.
 */
export const getSelectedAccount = (
  connection: AccountConnection,
  accountAddress?: string,
): KeyringPair => {
  let selectedAccount: KeyringPair | undefined;

  if (accountAddress) {
    selectedAccount = connection.accounts.get(accountAddress);
  } else {
    selectedAccount = Array.from(connection.accounts.values())[0];
  }

  if (!selectedAccount) {
    throw new Error(`Account ${accountAddress ?? ''} not found in session.`);
  }

  return selectedAccount;
};

/**
 * Retrieves the selected `KeyringPair` from the given connection if it is an `AccountConnection`.
 *
 * - If the connection has local `accounts` (i.e., it's an `AccountConnection`), it uses the provided `accountAddress`
 *   to select the appropriate account via `getSelectedAccount`.
 * - If the connection is a `WalletConnection`, returns `undefined`.
 *
 * @param {AccountConnection | WalletConnection} connection - The connection object which may contain accounts.
 * @param {string} [accountAddress] - Optional address of the account to select.
 * @returns {KeyringPair | undefined} - The selected `KeyringPair` if available, otherwise `undefined`.
 */
export function getKeyringAccountIfAvailable(
  connection: AccountConnection | WalletConnection,
  accountAddress?: string,
): KeyringPair | undefined {
  return 'accounts' in connection
    ? getSelectedAccount(connection, accountAddress)
    : undefined;
}

/**
 * Extracts a human-readable error message from various error types.
 *
 * @param err - The error object to extract a message from.
 * @returns A string message describing the error.
 */
export const extractErrorMessage = (err: unknown): string => {
  if (err instanceof Error) {
    return err.message;
  }

  if (typeof err === 'object' && err !== null) {
    const maybeError = err as Record<string, unknown>;
    if (typeof maybeError.error === 'string') {
      return maybeError.error;
    }
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }

  return String(err);
};

/**
 * Safe wrapper for emitting events without crashing.
 */
export const safeEmit = (
  emitter: EventEmitter,
  event: string,
  data: unknown,
) => {
  try {
    emitter.emit(event, data);
  } catch (error) {
    console.debug(`Failed to emit event ${event}:`, error);
  }
};

/**
 * Type guard for Groth16Config
 */
export function isGroth16Config(
  options: ProofOptions,
): options is ProofOptions & { config: Groth16Config } {
  return (
    options.proofType === ProofType.groth16 &&
    options.config !== undefined &&
    (options.config as Groth16Config).library !== undefined &&
    (options.config as Groth16Config).curve !== undefined
  );
}

/**
 * Type guard for Plonky2Config
 */
export function isPlonky2Config(
  options: ProofOptions,
): options is ProofOptions & { config: Plonky2Config } {
  return (
    options.proofType === ProofType.plonky2 &&
    options.config !== undefined &&
    (options.config as Plonky2Config).hashFunction !== undefined
  );
}

/**
 * Type guard for Risc0Config
 */
export function isRisc0Config(
  options: ProofOptions,
): options is ProofOptions & { config: Risc0Config } {
  return (
    options.proofType === ProofType.risc0 &&
    options.config !== undefined &&
    (options.config as Risc0Config).version !== undefined
  );
}

/**
 * Type guard for Ultraplonk Config
 */
export function isUltraplonkConfig(
  options: ProofOptions,
): options is ProofOptions & { config: UltraplonkConfig } {
  return (
    options.proofType === ProofType.ultraplonk &&
    options.config !== undefined &&
    (options.config as UltraplonkConfig).numberOfPublicInputs !== undefined
  );
}

/**
 * Type guard for Ultrahonk Config
 */
export function isUltrahonkConfig(
  options: ProofOptions,
): options is ProofOptions & {
  config: UltrahonkConfig & {
    variant: NonNullable<UltrahonkConfig['variant']>;
  };
} {
  return (
    options.proofType === ProofType.ultrahonk &&
    options.config !== undefined &&
    (options.config as UltrahonkConfig).variant !== undefined
  );
}

/**
 * Type guard for versioned Ultrahonk Config
 */
export function isVersionedUltrahonkConfig(
  options: ProofOptions,
): options is ProofOptions & { config: Required<UltrahonkConfig> } {
  return (
    isUltrahonkConfig(options) &&
    (options.config as UltrahonkConfig).version !== undefined
  );
}

/**
 * Type guard for TEE Config
 */
export function isTeeConfig(options: ProofOptions): options is ProofOptions & {
  config: TeeConfig & { variant: NonNullable<TeeConfig['variant']> };
} {
  return (
    options.proofType === ProofType.tee &&
    options.config !== undefined &&
    (options.config as TeeConfig).variant !== undefined
  );
}

// ADD_NEW_PROOF_TYPE if it has a config options object

/**
 * Type guard to check if an object is a SubmittableExtrinsic<'promise'>.
 *
 * A SubmittableExtrinsic is identified by the presence of a `signAsync` method.
 *
 * @param obj - The object to evaluate.
 * @returns True if the object is a SubmittableExtrinsic, otherwise false.
 */
function isSubmittableExtrinsic(
  obj: unknown,
): obj is SubmittableExtrinsic<'promise'> {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'signAsync' in obj &&
    typeof (obj as { signAsync: unknown }).signAsync === 'function'
  );
}

/**
 * Ensures the provided extrinsic is a SubmittableExtrinsic.
 * Converts a raw Extrinsic to Submittable if needed using the given API instance.
 *
 * @param extrinsic - A SubmittableExtrinsic or raw Extrinsic.
 * @param api - An instance of ApiPromise used to convert the extrinsic.
 * @returns A SubmittableExtrinsic<'promise'> ready to be signed and submitted.
 */
export function toSubmittableExtrinsic(
  extrinsic: SubmittableExtrinsic<'promise'> | Extrinsic,
  api: ApiPromise,
): SubmittableExtrinsic<'promise'> {
  if (isSubmittableExtrinsic(extrinsic)) {
    return extrinsic;
  }

  const call = api.createType('Call', extrinsic.method);
  return api.tx(call);
}

/**
 * Asserts that the given input is a 0x-prefixed hex string.
 *
 * @param input - The string to validate.
 * @returns The input unchanged, for ergonomic inline use.
 * @throws {Error} If the input does not start with `0x`.
 */
export function validateHexString(input: string): string {
  if (!input.startsWith('0x')) {
    throw new Error('Invalid format: string input must be 0x-prefixed.');
  }
  return input;
}
